// src/service/blogService.js
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// Curated starter articles for the JMI Jewellery B2B Platform
const INITIAL_BLOG_POSTS = [
  {
    slug: "bis-hallmarking-2-0-and-huid-compliance-b2b",
    title: "Understanding BIS Hallmarking 2.0 & HUID Tracking in Indian B2B Jewellery Trade",
    category: "Compliance & Hallmarking",
    categorySlug: "compliance",
    author: "JMI Regulatory Research Desk",
    authorRole: "Bureau of Indian Standards Advisory Group",
    readTime: "6 min read",
    coverImage: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1200&q=80",
    excerpt: "A comprehensive guide for retail jewellers and casting manufacturers on complying with 6-digit alphanumeric HUID requirements, laser tagging, and digital audit readiness.",
    tags: ["HUID", "BIS Hallmarking", "Regulatory Compliance", "Gold 22K"],
    publishedAt: "2026-03-12T10:00:00.000Z",
    status: "published",
    content: `## The Modern Landscape of BIS Hallmarking in India

The Bureau of Indian Standards (BIS) has revolutionized jewellery integrity across India with the mandatory implementation of unique 6-digit alphanumeric **Hallmark Unique Identification (HUID)** numbers. For B2B buyers and bullion dealers, this shift marks the transition from trust-based transactions to immutable cryptographic accountability.

### What HUID Means for B2B Procurements

Under the traditional system, hallmarked jewellery carried standard logos, karat indicators, and assay center stamps. However, batch tracing remained an administrative hurdle. With HUID:

1. **Individual Piece Traceability**: Every individual bangle, chain, necklace, or ring carries an exclusive laser-engraved 6-digit alphanumeric code.
2. **Assaying Center Accountability**: The code connects directly to the BIS server, identifying the exact Assaying and Hallmarking Centre (AHC) that tested the purity.
3. **Weight & Karat Verification**: Jewellers can cross-check declared gross weight and net gold content via the BIS Care app before accepting consignments.

> "HUID eliminates counterparty risk in wholesale jewellery sourcing. A retail showroom owner in Coimbatore can now purchase from a casting factory in Rajkot with total mathematical certainty."

---

### Key Compliance Steps for Manufacturers & Retailers

To ensure uninterrupted trading on the JMI B2B platform, all registered sellers and wholesale suppliers must maintain:

- **Active BIS Registration**: Valid certificate for all processing, refining, and casting units.
- **Automated Digital Invoicing**: GST proforma invoices must clearly display the registered HUID batches corresponding to the physical weight.
- **Zero-Tolerance Purity Tolerance**: Gold 22K (916) must consistently measure at or above 916.0 parts per thousand on XRF spectrometry.

### How JMI Enforces 100% Hallmarking Integrity

Every order processed through the **JMI Operating Layer** passes through regional operator verification hubs. Operators scan and record HUID laser stamps before sealing products into tamper-evident vault pouches, ensuring that what reaches the buyer's showroom matches the exact proforma ledger.`,
  },
  {
    slug: "gold-rate-dynamics-spot-usd-to-rtgs-formula",
    title: "The Mathematics of Indian Bullion Pricing: From International Spot USD to State RTGS Rates",
    category: "Bullion & Pricing Dynamics",
    categorySlug: "pricing",
    author: "Devendra Soni",
    authorRole: "Chief Bullion Strategist, JMI",
    readTime: "8 min read",
    coverImage: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Deconstructing how international XAU/USD spot prices convert to Indian Rupee benchmark rates, custom duty structures, bank premiums, and state-wise logistic adjustments.",
    tags: ["Gold Rates", "Spot Market", "RTGS Formula", "Currency Hedging"],
    publishedAt: "2026-03-14T14:30:00.000Z",
    status: "published",
    content: `## Why Gold Rates Differ Between International Spot and Local Markets

Every jeweller and bullion merchant monitors two numbers closely: the international spot price (quoted in USD per Troy Ounce) and the domestic Indian RTGS rate (quoted in INR per 10 grams). 

Understanding the mathematical bridge between these two benchmarks is essential for profitable manufacturing and inventory hedging.

### The Core Indian Base Rate Equation

The standard formula adopted by the **JMI Rate Engine** calculates pure 24K bullion valuation through the following sequence:

\`\`\`
1 Troy Ounce (oz) = 31.1034768 Grams

Step 1: Raw INR per Gram = (Spot USD/oz × USD/INR Exchange Rate) ÷ 31.1035
Step 2: Base Bullion Rate (with Import Duties & Bank Premium) = Raw INR × (1 + SuperAdmin Premium%)
Step 3: 10g 24K Benchmark = Base Bullion Rate × 10
Step 4: 22K (916) Hallmark Rate = 10g 24K Benchmark × (22 ÷ 24)
\`\`\`

### Impact of State-Level Logistics & Regional Demand Constraints

While the national benchmark provides the base valuation, regional market friction creates localized variances:

- **Maharashtra & Gujarat (Western Hubs)**: Direct port access in Mumbai and major refining clusters in Ahmedabad lead to razor-thin spot spreads.
- **Northern & Eastern Markets (Delhi, Kolkata, UP)**: Additional armored transport transit costs and local vault insurance add state constraint markups.
- **Southern Markets (Tamil Nadu, Kerala)**: High daily physical consumption volume often influences local scrap return discounts and RTGS premium spreads.

---

### Decoupled Pricing: The JMI Advantage

Traditional wholesale jewellery quotations bundle making charges, wastage margins, and bullion rates into a single opaque price. JMI eliminates this opacity:

> **Pure Bullion Rate (Locked in Real-Time)** + **Transparent Net Weight** + **Itemized Making Charges (per gram or piece)** = **True Fair Value**.

This ensures that retail jewellers never overpay for metal during volatile market swings.`,
  },
  {
    slug: "modern-cad-cam-and-laser-sintering-in-b2b-jewellery",
    title: "Scaling B2B Jewellery Manufacturing: How CAD/CAM & Direct Metal Laser Sintering (DMLS) Are Changing Wholesale",
    category: "Manufacturing Technology",
    categorySlug: "technology",
    author: "Rituja Parmar",
    authorRole: "Head of Jewellery Engineering, JMI",
    readTime: "5 min read",
    coverImage: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Explore how state-of-the-art 3D resin printing, continuous casting, and hollow electroforming allow manufacturers to produce lightweight high-volume bridal collections.",
    tags: ["CAD/CAM", "3D Printing", "Lightweight Jewellery", "Manufacturing"],
    publishedAt: "2026-03-15T09:15:00.000Z",
    status: "published",
    content: `## The Shift from Hand-Carved Master Waxes to Parametric 3D Engineering

For centuries, Indian jewellery manufacturing relied entirely on manual *karigari* for carving master wax designs. While artisanal handcrafting remains irreplaceable for bespoke heritage polki and bridal heirlooms, modern commercial retail demands lightweight, structural precision, and rapid repeat manufacturing.

### Three Technologies Transforming B2B Jewellery Production

#### 1. High-Resolution DLP & SLA 3D Resin Printing
Modern additive manufacturing allows casting factories to print hundreds of intricate ring shanks, delicate pendant filigrees, and stone-setting prongs in under two hours with 25-micron accuracy.

#### 2. Hollow Electroforming & CNC Tube Technology
With gold prices sustaining historic highs, consumers demand bridal aesthetics at accessible price points. Electroforming allows 22K gold bangles with the visual presence of 40 grams to weigh merely 16 to 18 grams while maintaining structural resilience.

#### 3. Automated Laser Prong Setting for Diamonds
Automated optical vision systems align diamond facets and secure micro-prongs at ten times the speed of manual setters, resulting in flawless light refraction and zero stone dislodgement.

---

### How JMI Connects High-Tech Manufacturers with Retail Showrooms

The **JMI Seller App** enables modern manufacturing facilities to upload detailed CAD specs, 360-degree high-definition video renders, and accurate weight tolerance brackets. Retailers across Tier-2 and Tier-3 cities can order state-of-the-art catalog designs on demand without holding heavy dead inventory.`,
  },
];

class BlogService {
  constructor() {
    this.cachedPosts = null;
  }

  // Fetch all published posts for public view
  async getPublishedPosts(category = null) {
    try {
      const q = query(
        collection(db, "blogPosts"),
        where("status", "==", "published"),
        orderBy("publishedAt", "desc")
      );
      const snap = await getDocs(q);
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

      if (list.length === 0) {
        // If Firestore is empty, seed with initial posts
        await this.seedInitialPosts();
        return INITIAL_BLOG_POSTS;
      }

      if (category && category !== "all") {
        return list.filter((p) => p.categorySlug === category || p.category === category);
      }
      return list;
    } catch (err) {
      console.warn("[BlogService] Falling back to default posts:", err.message);
      if (category && category !== "all") {
        return INITIAL_BLOG_POSTS.filter((p) => p.categorySlug === category);
      }
      return INITIAL_BLOG_POSTS;
    }
  }

  // Fetch all posts (including drafts) for author dashboard
  async getAllPostsForAdmin() {
    try {
      const snap = await getDocs(collection(db, "blogPosts"));
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

      if (list.length === 0) {
        await this.seedInitialPosts();
        return INITIAL_BLOG_POSTS.map((p, i) => ({ id: `seed-${i}`, ...p }));
      }

      return list.sort((a, b) => {
        const tA = new Date(a.publishedAt || a.createdAt || 0);
        const tB = new Date(b.publishedAt || b.createdAt || 0);
        return tB - tA;
      });
    } catch (err) {
      console.warn("[BlogService] Admin fetch fallback:", err.message);
      return INITIAL_BLOG_POSTS.map((p, i) => ({ id: `seed-${i}`, ...p }));
    }
  }

  // Fetch a single post by slug
  async getPostBySlug(slug) {
    try {
      const q = query(
        collection(db, "blogPosts"),
        where("slug", "==", slug)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    } catch (err) {
      console.warn("[BlogService] Single post lookup error:", err);
    }
    // Fallback to initial posts array
    const found = INITIAL_BLOG_POSTS.find((p) => p.slug === slug);
    return found ? { id: slug, ...found } : null;
  }

  // Create a new blog post
  async createPost(postData) {
    const cleanSlug = postData.slug || postData.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const newDoc = {
      ...postData,
      slug: cleanSlug,
      createdAt: Timestamp.now(),
      publishedAt: postData.status === "published" ? new Date().toISOString() : null,
    };

    const docRef = await addDoc(collection(db, "blogPosts"), newDoc);
    return { id: docRef.id, ...newDoc };
  }

  // Update existing blog post
  async updatePost(id, postData) {
    const docRef = doc(db, "blogPosts", id);
    const updateData = {
      ...postData,
      updatedAt: Timestamp.now(),
    };
    if (postData.status === "published" && !postData.publishedAt) {
      updateData.publishedAt = new Date().toISOString();
    }
    await updateDoc(docRef, updateData);
    return { id, ...updateData };
  }

  // Delete blog post
  async deletePost(id) {
    const docRef = doc(db, "blogPosts", id);
    await deleteDoc(docRef);
    return true;
  }

  // Seed default initial posts into Firestore
  async seedInitialPosts() {
    try {
      for (const p of INITIAL_BLOG_POSTS) {
        await addDoc(collection(db, "blogPosts"), {
          ...p,
          createdAt: Timestamp.now(),
        });
      }
    } catch (e) {
      console.warn("[BlogService] Auto-seed failed:", e.message);
    }
  }
}

export const blogService = new BlogService();
